import { useCallback } from 'react';
import type { CalDateInfo, SlotInfo } from '../../../types/schedule.types';
import type { SceneInfo } from '../../../types/scene.types';
import {
  formatDateToString as formatDateToStringUtil,
  getDateCalDate as getDateCalDateUtil,
  getDateSlots as getDateSlotsUtil,
  getCalendarRowIdsForDateStr as getCalendarRowIdsForDateStrUtil,
  calDateDtToLocalStr as calDateDtToLocalStrUtil,
} from '../utils/slotSelectors';
import { extractDateFromOverId, checkSlotDuplicate } from '../utils/slotDragUtils';

export function useScheduleSelectors(params: {
  optimisticSlots: SlotInfo[];
  optimisticCalDates: CalDateInfo[];
  getSceneInfo: (sceneID: string | undefined) => SceneInfo | undefined;
}) {
  const { optimisticSlots, optimisticCalDates, getSceneInfo } = params;

  const formatDateToString = useCallback((date: Date): string => formatDateToStringUtil(date), []);

  const calDateDtToLocalStr = useCallback((dt: string | undefined): string => calDateDtToLocalStrUtil(dt), []);

  const getDateSlots = useCallback(
    (dateStr: string): SlotInfo[] => getDateSlotsUtil(dateStr, optimisticSlots),
    [optimisticSlots],
  );

  const getDateCalDate = useCallback(
    (dateStr: string): CalDateInfo | undefined => getDateCalDateUtil(dateStr, optimisticCalDates),
    [optimisticCalDates],
  );

  const getCalendarRowIdsForDateStr = useCallback(
    (dateStr: string) =>
      getCalendarRowIdsForDateStrUtil(dateStr, optimisticSlots, optimisticCalDates, getSceneInfo),
    [optimisticSlots, optimisticCalDates, getSceneInfo],
  );

  const checkSlotDuplicateFn = useCallback(
    (sceneID: string, dateStr: string, excludeSlotID?: string, scheduleIDParam?: string) =>
      checkSlotDuplicate(sceneID, dateStr, excludeSlotID, scheduleIDParam, optimisticSlots),
    [optimisticSlots],
  );

  const extractDateFromOverIdFn = useCallback(
    (overId: string, overData?: { dateStr?: string } | null) =>
      extractDateFromOverId(overId, overData, optimisticSlots),
    [optimisticSlots],
  );

  return {
    formatDateToString,
    calDateDtToLocalStr,
    getDateSlots,
    getDateCalDate,
    getCalendarRowIdsForDateStr,
    checkSlotDuplicateFn,
    extractDateFromOverIdFn,
  };
}

